import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import {
  FaPaperPlane, FaUsers, FaGlobe, FaSpinner, FaArrowLeft,
  FaLanguage, FaComments, FaUserPlus, FaSignOutAlt
} from 'react-icons/fa';

interface ChatRoom {
  id: string;
  name: string;
  description: string | null;
  is_public: boolean;
  created_by: string;
  max_participants: number;
  participant_count?: number;
  created_at?: string;
}

interface ChatMessage {
  id: string;
  room_id: string;
  user_id: string;
  original_message: string;
  original_language: 'english' | 'french' | 'haitian_creole';
  spanish_translation: string | null;
  is_translated: boolean;
  created_at: string;
  user_email?: string;
  user_profile_image?: string;
}

interface ChatParticipant {
  id: string;
  room_id: string;
  user_id: string;
  joined_at: string;
  last_seen: string | null;
  is_active: boolean;
  user_email?: string;
  user_profile_image?: string;
}

export default function PracticeChat() {
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [participants, setParticipants] = useState<ChatParticipant[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'french' | 'haitian_creole'>('english');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [translatingMessage, setTranslatingMessage] = useState(false);
  const [showRoomList, setShowRoomList] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [newRoomIsPublic, setNewRoomIsPublic] = useState(true);
  const [creatingRoom, setCreatingRoom] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = getCurrentUser(); // your existing site-wide auth profile (kept!)

  // Minimal helper: check if Supabase has a session, but do NOT redirect
  const ensureAuth = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) console.error('getSession error:', error);
    if (!session?.user) {
      toast.error('Your session is missing. Please re-open this page or log in again.');
      return false;
    }
    return true;
  };

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load rooms on mount; do NOT force login redirect here
  useEffect(() => {
    (async () => {
      await loadRooms();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedRoom) return;

    loadMessages();
    loadParticipants();
    joinRoom();

    // Real-time subscriptions
    const messagesSubscription = supabase
      .channel(`room-${selectedRoom.id}-messages`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'stripe_chat_messages',
        filter: `room_id=eq.${selectedRoom.id}`,
      }, (payload) => {
        const newMsg = payload.new as ChatMessage;
        setMessages(prev => [...prev, newMsg]);
        scrollToBottom();
      })
      .subscribe();

    const participantsSubscription = supabase
      .channel(`room-${selectedRoom.id}-participants`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'stripe_chat_participants',
        filter: `room_id=eq.${selectedRoom.id}`,
      }, () => {
        loadParticipants();
      })
      .subscribe();

    return () => {
      messagesSubscription.unsubscribe();
      participantsSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadRooms = async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_chat_rooms')
        .select(`
          *,
          participant_count:stripe_chat_participants(count)
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const roomsWithCount: ChatRoom[] = (data || []).map((room: any) => ({
        ...room,
        participant_count: room.participant_count?.[0]?.count || 0,
      }));

      setRooms(roomsWithCount);
      if (roomsWithCount.length > 0 && !selectedRoom) setSelectedRoom(roomsWithCount[0]);
    } catch (err: any) {
      console.error('Error loading rooms:', err);
      toast.error('Failed to load chat rooms');
    }
  };

  const loadMessages = async () => {
    if (!selectedRoom) return;
    try {
      const { data, error } = await supabase
        .from('stripe_chat_messages')
        .select(`
          *,
          user_email:stripeusers!stripe_chat_messages_user_id_fkey(email, profile_image)
        `)
        .eq('room_id', selectedRoom.id)
        .order('created_at', { ascending: true })
        .limit(50);

      if (error) throw error;

      const formatted = (data || []).map((msg: any) => ({
        ...msg,
        user_email: msg.user_email?.email,
        user_profile_image: msg.user_email?.profile_image,
      })) as ChatMessage[];

      setMessages(formatted);
    } catch (err: any) {
      console.error('Error loading messages:', err);
      toast.error('Failed to load messages');
    }
  };

  const loadParticipants = async () => {
    if (!selectedRoom) return;
    try {
      const { data, error } = await supabase
        .from('stripe_chat_participants')
        .select(`
          *,
          user_email:stripeusers!stripe_chat_participants_user_id_fkey(email, profile_image)
        `)
        .eq('room_id', selectedRoom.id)
        .eq('is_active', true)
        .order('joined_at', { ascending: true });

      if (error) throw error;

      const formatted = (data || []).map((p: any) => ({
        ...p,
        user_email: p.user_email?.email,
        user_profile_image: p.user_email?.profile_image,
      })) as ChatParticipant[];

      setParticipants(formatted);
    } catch (err: any) {
      console.error('Error loading participants:', err);
    }
  };

  const joinRoom = async () => {
    if (!selectedRoom || !user?.id) return;
    // Only enforce session check at action time
    if (!(await ensureAuth())) return;

    try {
      const { data: existing } = await supabase
        .from('stripe_chat_participants')
        .select('id')
        .eq('room_id', selectedRoom.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!existing) {
        const { error } = await supabase
          .from('stripe_chat_participants')
          .insert({
            room_id: selectedRoom.id,
            user_id: user.id,
            is_active: true,
          });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('stripe_chat_participants')
          .update({
            last_seen: new Date().toISOString(),
            is_active: true,
          })
          .eq('id', existing.id);
        if (error) throw error;
      }
    } catch (err: any) {
      console.error('Error joining room:', err);
    }
  };

  const leaveRoom = async () => {
    if (!selectedRoom || !user?.id) return;
    try {
      const { error } = await supabase
        .from('stripe_chat_participants')
        .update({ is_active: false })
        .eq('room_id', selectedRoom.id)
        .eq('user_id', user.id);
      if (error) throw error;
    } catch (err: any) {
      console.error('Error leaving room:', err);
    }
  };

  const translateMessage = async (message: string, fromLanguage: string): Promise<string> => {
    try {
      setTranslatingMessage(true);
      const response = await fetch('/api/translate-to-spanish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: message, fromLanguage }),
      });
      if (!response.ok) throw new Error('Translation failed');
      const data = await response.json();
      return data.translation;
    } catch (err: any) {
      console.error('Translation error:', err);
      throw new Error('Failed to translate message');
    } finally {
      setTranslatingMessage(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom || !user?.id || sendingMessage) return;
    if (!(await ensureAuth())) return;

    setSendingMessage(true);
    try {
      const spanishTranslation = await translateMessage(newMessage.trim(), selectedLanguage);

      const { error } = await supabase
        .from('stripe_chat_messages')
        .insert({
          room_id: selectedRoom.id,
          user_id: user.id,
          original_message: newMessage.trim(),
          original_language: selectedLanguage,
          spanish_translation: spanishTranslation,
          is_translated: true,
        });

      if (error) throw error;

      setNewMessage('');

      await supabase
        .from('stripe_chat_participants')
        .update({ last_seen: new Date().toISOString() })
        .eq('room_id', selectedRoom.id)
        .eq('user_id', user.id);
    } catch (err: any) {
      console.error('Error sending message:', err);
      toast.error(err?.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const createRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      toast.error('Please log in to create a chat room');
      navigate('/login');
      return;
    }
    
    if (!newRoomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }

    setCreatingRoom(true);
    try {
      const { data, error } = await supabase
        .from('stripe_chat_rooms')
        .insert({
          name: newRoomName.trim(),
          description: newRoomDescription.trim() || null,
          is_public: newRoomIsPublic,
          created_by: user.id,
          max_participants: 50,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Chat room created successfully!');
      setShowCreateRoomModal(false);
      setNewRoomName('');
      setNewRoomDescription('');
      setNewRoomIsPublic(true);

      await loadRooms();
      setSelectedRoom(data);
      if (isMobile) setShowRoomList(false);
    } catch (err: any) {
      console.error('Error creating room:', err);
      toast.error(error.message || 'Failed to create chat room');
    } finally {
      setCreatingRoom(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getLanguageFlag = (language: string) => {
    switch (language) {
      case 'english': return '🇺🇸';
      case 'french': return '🇫🇷';
      case 'haitian_creole': return '🇭🇹';
      default: return '🌐';
    }
  };

  const getLanguageLabel = (language: string) => {
    switch (language) {
      case 'english': return 'English';
      case 'french': return 'French';
      case 'haitian_creole': return 'Haitian Creole';
      default: return language;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading chat rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex">
      {/* Room List Sidebar */}
      <div className={`${isMobile ? (showRoomList ? 'w-full' : 'hidden') : 'w-80'} bg-black/50 backdrop-blur-lg border-r border-gray-800 flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FaComments className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-bold text-white">Practice Chat</h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowCreateRoomModal(true)}
                className="p-2 rounded-full bg-yellow-400/20 hover:bg-yellow-400/30 transition-colors border border-yellow-400/30"
                title="Create new chat room"
              >
                <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <Link
                to="/dashboard"
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <FaArrowLeft className="w-4 h-4 text-gray-400 hover:text-white" />
              </Link>
            </div>
          </div>
          <p className="text-gray-400 text-sm">
            Practice Spanish with other learners from around the world
          </p>
        </div>

        {/* Room List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {rooms.map((room) => (
            <button
              key={room.id}
              onClick={() => {
                setSelectedRoom(room);
                if (isMobile) setShowRoomList(false);
              }}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                selectedRoom?.id === room.id
                  ? 'bg-yellow-400/20 border-yellow-400/50 text-white'
                  : 'bg-white/5 border-white/10 hover:bg-white/10 text-white/80'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">{room.name}</h3>
                <div className="flex items-center space-x-1">
                  <FaUsers className="w-3 h-3" />
                  <span className="text-xs">{room.participant_count || 0}</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 line-clamp-2">{room.description || ''}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${isMobile ? (showRoomList ? 'hidden' : 'w-full') : 'flex-1'} flex flex-col`}>
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-800 bg-black/50 backdrop-blur-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {isMobile && (
                    <button
                      onClick={() => setShowRoomList(true)}
                      className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                      <FaArrowLeft className="w-4 h-4 text-white" />
                    </button>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-white">{selectedRoom.name}</h2>
                    <p className="text-sm text-gray-400">{participants.length} participants online</p>
                  </div>
                </div>

                {/* Language Selector */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400">Speaking:</span>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value as any)}
                    className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm focus:ring-2 focus:ring-yellow-400"
                  >
                    <option value="english">🇺🇸 English</option>
                    <option value="french">🇫🇷 French</option>
                    <option value="haitian_creole">🇭🇹 Haitian Creole</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ height: 'calc(100vh - 200px)' }}>
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${message.user_id === user?.id ? 'order-2' : 'order-1'}`}>
                    {/* User Info */}
                    <div className={`flex items-center space-x-2 mb-1 ${message.user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700">
                        {message.user_profile_image ? (
                          <img src={message.user_profile_image} alt="User" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white">
                            {message.user_email?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">{message.user_email}</span>
                      <span className="text-xs">{getLanguageFlag(message.original_language)}</span>
                      <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
                    </div>

                    {/* Message Bubbles */}
                    <div className="space-y-2">
                      {/* Spanish Translation (Primary) */}
                      {message.spanish_translation && (
                        <div className={`p-3 rounded-xl ${
                          message.user_id === user?.id
                            ? 'bg-yellow-500 text-black rounded-br-md'
                            : 'bg-white/10 backdrop-blur-lg text-white border border-white/10 rounded-bl-md'
                        }`}>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs font-medium opacity-80">🇪🇸 Spanish:</span>
                          </div>
                          <p className="text-sm font-medium">{message.spanish_translation}</p>
                        </div>
                      )}

                      {/* Original Message (Secondary) */}
                      <div className={`p-2 rounded-lg text-xs ${
                        message.user_id === user?.id
                          ? 'bg-yellow-400/20 text-yellow-200'
                          : 'bg-gray-800/50 text-gray-300'
                      }`}>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium opacity-80">
                            {getLanguageFlag(message.original_language)} {getLanguageLabel(message.original_language)}:
                          </span>
                        </div>
                        <p className="opacity-90">{message.original_message}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {translatingMessage && (
                <div className="flex justify-center">
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-lg rounded-xl p-3 border border-white/20">
                    <FaSpinner className="w-4 h-4 animate-spin text-yellow-400" />
                    <span className="text-white/80 text-sm">Translating to Spanish...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-800 bg-black/50 backdrop-blur-lg">
              <form onSubmit={sendMessage} className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Type your message in ${getLanguageLabel(selectedLanguage)}...`}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    disabled={sendingMessage || translatingMessage}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-xs text-gray-400">
                      {getLanguageFlag(selectedLanguage)}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={sendingMessage || translatingMessage || !newMessage.trim()}
                  className="p-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl hover:from-yellow-500 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
                >
                  {sendingMessage || translatingMessage ? (
                    <FaSpinner className="w-5 h-5 animate-spin" />
                  ) : (
                    <FaPaperPlane className="w-5 h-5" />
                  )}
                </button>
              </form>

              <div className="mt-2 text-center">
                <p className="text-xs text-gray-400">
                  Your message will be automatically translated to Spanish for practice
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <FaComments className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Select a Chat Room</h3>
              <p className="text-gray-400">Choose a room to start practicing Spanish with other learners</p>
            </div>
          </div>
        )}
      </div>

      {/* Participants Sidebar - Desktop Only */}
      {!isMobile && selectedRoom && (
        <div className="w-64 bg-black/30 backdrop-blur-lg border-l border-gray-800 p-4">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <FaUsers className="w-4 h-4 mr-2 text-yellow-400" />
            Participants ({participants.length})
          </h3>
          <div className="space-y-2">
            {participants.map((participant) => (
              <div key={participant.id} className="flex items-center space-x-3 p-2 rounded-lg bg-white/5">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-700">
                  {participant.user_profile_image ? (
                    <img src={participant.user_profile_image} alt="User" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white">
                      {participant.user_email?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {participant.user_email}
                  </p>
                  <p className="text-xs text-gray-400">
                    {participant.user_id === user?.id ? 'You' : 'Online'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Room Modal */}
      {showCreateRoomModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl shadow-xl max-w-md w-full border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <FaUserPlus className="w-5 h-5 mr-3 text-yellow-400" />
                  Create Chat Room
                </h3>
                <button
                  onClick={() => setShowCreateRoomModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={createRoom} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Room Name *
                </label>
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="Enter room name..."
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  required
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={newRoomDescription}
                  onChange={(e) => setNewRoomDescription(e.target.value)}
                  placeholder="Describe what this room is for..."
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Room Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roomType"
                      checked={newRoomIsPublic}
                      onChange={() => setNewRoomIsPublic(true)}
                      className="w-4 h-4 text-yellow-400 bg-gray-800 border-gray-600 focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="ml-3 text-white">
                      <span className="font-medium">Public</span>
                      <span className="block text-sm text-gray-400">Anyone can join this room</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roomType"
                      checked={!newRoomIsPublic}
                      onChange={() => setNewRoomIsPublic(false)}
                      className="w-4 h-4 text-yellow-400 bg-gray-800 border-gray-600 focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="ml-3 text-white">
                      <span className="font-medium">Private</span>
                      <span className="block text-sm text-gray-400">Only invited users can join</span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <FaGlobe className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-yellow-200 mb-1">Spanish Practice Room</h4>
                    <p className="text-yellow-100/80 text-sm">
                      All messages will be automatically translated to Spanish to help everyone practice together!
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateRoomModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingRoom || !newRoomName.trim()}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-black font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  {creatingRoom ? (
                    <>
                      <FaSpinner className="animate-spin w-4 h-4 mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaUserPlus className="w-4 h-4 mr-2" />
                      Create Room
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
